<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      Planted Trees by Forest Platform
    </v-card-title>
    <v-card-subtitle>
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedPlatformType"
            :items="platformTypes"
            item-text="name"
            item-value="id"
            label="Select platform type"
            clearable
            @change="debounceFetching"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedProject"
            :items="projects"
            item-text="name"
            item-value="id"
            label="Select project"
            clearable
            @change="debounceFetching"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedTreeType"
            :items="treeTypes"
            item-text="name"
            item-value="id"
            label="Select tree type"
            clearable
            @change="debounceFetching"
          ></v-select>
        </v-col>
      </v-row>
    </v-card-subtitle>
    <v-spacer></v-spacer>
    <v-data-table
      :headers="headers"
      :items="treeReports"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalAmount"
      :footer-props="{
        'items-per-page-options': $options.DEFAULT_ITEMS_PER_PAGE_OPTIONS,
      }"
    >
      <template v-slot:[`item.forest`]="{ item }">
        <v-menu
          v-model="item.showForestSelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <span v-on="on" style="cursor: pointer; text-decoration: underline">
              <span v-if="item.forest.name && item.forest.name.length > 30">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ truncateText(item.forest.name) }}</span>
                  </template>
                  <span>{{ item.forest.name }}</span>
                </v-tooltip>
              </span>
              <span v-else>
                {{ item.forest.name }}
              </span>
            </span>
          </template>
          <v-list dense>
            <v-list-item style="cursor: pointer">
              <v-list-item-title class="font-weight-medium">
                <a
                  :href="`${$options.FOREST_DOMAIN}/${item.forest.slug}`"
                  target="_blank"
                  style="text-decoration: none"
                >
                  Go to Forest Page 🔗
                </a>
                <v-icon>mdi-open-in-new</v-icon>
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              :to="{ name: 'Forest', params: { id: item.forest.id } }"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Open Forest
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestEntity(item, 'Forest ID', item.forest.id)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest ID
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestEntity(item, 'Forest Name', item.forest.name)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest Name
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestEntity(item, 'Forest Slug', item.forest.slug)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest Slug
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.platformType`]="{ item }">
        <platforms-logo :platforms="[item.platformType]"></platforms-logo>
      </template>
      <template v-slot:[`item.projectId`]="{ item }">
        {{
          PROJECT_TYPES.find((p) => Number(p.id) === Number(item.projectId))
            .name
        }}
      </template>
      <template v-slot:[`item.countingTimestamp`]="{ item }">
        <span v-if="item.confirmed">
          <v-menu
            v-model="item.showPlantingDateSelector"
            :close-on-content-click="false"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <span
                v-on="on"
                style="cursor: pointer; text-decoration: underline"
              >
                {{ formatDate(item.countingTimestamp * 1000) }}
              </span>
            </template>
            <v-divider></v-divider>
            <v-list dense>
              <v-list-item
                @click="
                  copyPlantingDateEntity(
                    item,
                    'Planting Timestamp',
                    item.countingTimestamp
                  )
                "
                style="cursor: pointer"
              >
                <v-list-item-title class="font-weight-medium">
                  Copy Planting Timestamp
                </v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item
                @click="
                  copyPlantingDateEntity(
                    item,
                    'Stripe Subscription ID',
                    item.stripeSubscriptionId
                  )
                "
                style="cursor: pointer"
              >
                <v-list-item-title class="font-weight-medium">
                  Copy Stripe Subscription Id
                </v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item
                @click="
                  copyPlantingDateEntity(
                    item,
                    'Stripe Subscription Item ID',
                    item.stripeSubscriptionItemId
                  )
                "
                style="cursor: pointer"
              >
                <v-list-item-title class="font-weight-medium">
                  Copy Stripe Subscription Item ID
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </span>
      </template>
      <template v-slot:[`item.treeType`]="{ item }">
        <span v-if="item.treeType">
          {{
            TREE_TYPES.find((treeType) => treeType.id === item.treeType).name
          }}
        </span>
        <span v-else>
          {{ item.treeType }}
        </span>
      </template>
    </v-data-table>

    <!-- Success message snackbar -->
    <success-snackbar v-model="showSuccessMessage" :message="successMessage" />
  </v-card>
</template>

<script>
import { API } from "../../api";
import { formatDate, truncateText } from "../../helpers";
import { PROJECT_TYPES, PLATFORM_TYPES, TREE_TYPES } from "../../constants";
import { writeToClipboard } from "../../utils/copyText";
import SuccessSnackbar from "../Base/SuccessSnackbar.vue";
import PlatformsLogo from "../Base/PlatformsLogo.vue";

export default {
  name: "TreeReportsTable",
  FOREST_DOMAIN: process.env.VUE_APP_FOREST_DOMAIN,
  components: {
    PlatformsLogo,
    SuccessSnackbar,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      formatDate,
      truncateText,
      PLATFORM_TYPES,
      PROJECT_TYPES,
      TREE_TYPES,
      treeReports: [],
      totalAmount: 0,
      loading: false,
      showSuccessMessage: false,
      successMessage: "",
      copiedKey: null,
      resetTimer: null,
      headers: [
        {
          text: "Forest",
          value: "forest",
          width: 200,
          sortable: false,
        },
        {
          text: "Platform",
          value: "platformType",
          width: 100,
          sortable: false,
        },
        {
          text: "Project",
          value: "projectId",
          width: 150,
          sortable: false,
        },
        {
          text: "Planted At",
          value: "countingTimestamp",
          width: 150,
        },
        {
          text: "Tree Amount",
          value: "reviewAmount",
          align: "center",
          sortable: false,
          width: 100,
        },
        {
          text: "Tree Type",
          sortable: false,
          value: "treeType",
          width: 150,
        },
      ],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
        previousPage: 0,
      },
      selectedTreeType: null,
      treeTypes: TREE_TYPES,
      selectedPlatformType: null,
      platformTypes: PLATFORM_TYPES,
      selectedProject: null,
      projects: PROJECT_TYPES,
    };
  },
  created() {
    this.getTreeReports();
  },
  watch: {
    options: {
      handler: "debounceFetching",
      deep: true,
    },
  },
  methods: {
    debounceFetching() {
      if (this.timerId) {
        clearTimeout(this.timerId);
      }

      this.timerId = setTimeout(() => {
        this.getTreeReports();
      }, 500);
    },
    async getTreeReports() {
      this.loading = true;
      try {
        const getPlantingReportForTable = await API.getPlantingReports({
          ownedBy: this.id,
          confirmed: true,
          sortBy: this.options.sortBy[0],
          order: this.options.sortDesc[0] ? "desc" : "asc",
          page: this.options.page,
          pageSize:
            this.options.itemsPerPage === -1 ? 0 : this.options.itemsPerPage,
          treeType: this.selectedTreeType,
          platformType: this.selectedPlatformType,
          projectId: this.selectedProject,
        });

        this.treeReports = getPlantingReportForTable.data;
        this.totalAmount = getPlantingReportForTable.count;

        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    async copyToClipboard(label, value, key) {
      try {
        await writeToClipboard(String(value));
        this.copiedKey = key;
        // Show success message
        this.successMessage = `${label} successfully copied to clipboard!`;
        this.showSuccessMessage = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to copy " + label, e);
      } finally {
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => (this.copiedKey = null), 1500);
      }
    },
    copyForestEntity(item, type, value) {
      item.showForestSelector = false;
      this.copyToClipboard(type, value, type);
    },
    copyCompanyEntity(item, type, value) {
      item.showCompanySelector = false;
      this.copyToClipboard(type, value, type);
    },
    copyPlantingDateEntity(item, type, value) {
      item.showPlantingDateSelector = false;
      this.copyToClipboard(type, value, type);
    },
  },
};
</script>
